// packages
import { useEffect, useRef, useState } from 'react';
import {
  defaultDropAnimationSideEffects,
  DndContext,
  DropAnimation,
  PointerSensor,
  pointerWithin,
  TouchSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { format, getISOWeek } from 'date-fns';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { Box, styled, Tooltip, Typography } from '@mui/material';
import Chip from '@sdb/ui/atoms/Chip';

// components
import { LoadingOverlay } from 'components/shared';
import { ConditionalDraggableCard } from '../components/ActivityCard/ActivityCard';
import DroppableCell from '../components/DroppableCell/DroppableCell';
import ActivityPanel from '../components/ActivityPanel/ActivityPanel';
import { PopOverConfirmationContext } from '../PopOverConfirmationContext';
import EventCard from './EventCard/EventCard';
import EventPlaceholder from './EventPlaceholder/EventPlaceholder';
import KidsTable from './KidsTable';
import TaskList from 'components/shared/TaskList/TaskList';
import ToPlanDropzone from '../components/InnerList/ToPlanDropzone';
import SortedToPlanList from '../components/ToPlanSidebar/SortedToPlanList';
import DragPortal from '../components/DraggableOverlay/DragPortal';
import Absence from './Absence/Absence';
import Availability from './Availability';

// hooks
import { useNavParams, useSelector } from 'hooks';
import { useRecoilState, useRecoilValue } from 'recoil';
import useCopyMode, { CopyModeReturn } from '../hooks/useCopyMode';
import useConnectScheduleAction from '../hooks/useConnectScheduleAction';
import { UsePlanningReturn } from '../hooks/usePlanning';
import usePlanningEntity from '../hooks/usePlanningEntity';
import useSelectedDate from '../hooks/useSelectedDate';
import { useDateNavigationStore } from 'components/shared/DateNavigation/DateNavigation.stores';

// styles
import { GRAY_LIGHT_TERTIARY, PRIMARY, WHITE } from 'constants/colors';
import { CARD_WIDTH } from 'constants/widths';
import { ZoomLevel } from '../components/Header/ZoomLevelMenu';

// utils
import {
  APNotification,
  appendCorrelationToken,
  findItem,
  getAbsencesOrAvailabilitiesByWeekDay,
  getConflictingEvents,
  getEventsByWeekDay,
  isWithinDay,
  notificationsToHashTable,
  planningEntitiesToEvents,
  handlePlanAppointments as handlePlanAppointmentsUtil,
  unassignAppointment as unassignAppointmentUtil
} from '../utils';
import { formatHours, getWeekInterval, getWorkweekInterval } from 'utils/date';
import { getRosterDays } from 'components/shared/roster/utils';
import { showErrorSnackbar } from 'components/shared/Snackbar';
import { getCurrentlyLoggedInEmployeeId } from 'utils/misc';

// types
import type {
  AbsenceResponse,
  AvailabilityResponse,
  PlanningEntityType
} from 'generated';
import { PlanAppointmentsRequest } from 'generated';
import { Event, Schedule } from '../types';
import { EditAppointmentType } from 'RestAPIs/activities/types';
import { ConnectAppointmentGroupingPayload } from 'RestAPIs/activities';
import { RosterDay } from 'components/shared/roster/types';
import { View } from '../enums';

// atoms
import {
  dialogManagerActiveAtom,
  dragActionActiveAtom,
  planResponsePlanningEntityIdentifiersAtom,
  planUnitsAtom,
  shouldShowPopoverDialogAtom
} from '../atoms';

// mutations
import { mutations } from 'RestAPIs/rapini';
import { DialogType } from '../components/DialogManager/types';

dayjs.extend(isoWeek);

const dropAnimation: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.5'
      }
    }
  })
};

const Table = styled('table')`
  width: 100vw;
  border-collapse: collapse;
  table-layout: fixed;
`;

const TableHeaderDateRow = styled('tr')`
  position: sticky;
  top: 0;
  z-index: 40;
  height: 50px;
  background-color: ${WHITE};
  box-shadow: inset 0 -1px 0 ${PRIMARY};
`;

const TableHeaderDateCell = styled('th')`
  min-width: ${CARD_WIDTH}px;
`;

const TableCell = styled('td')`
  min-width: ${CARD_WIDTH}px;
  height: 1px; // See: https://stackoverflow.com/a/3542470
  padding: 4px;
`;

function getSubtitle(
  event: Event,
  planningEntity: PlanningEntityType,
  showEvents: boolean
) {
  switch (planningEntity) {
    case 'CLIENT':
      return event?.employees?.[0]?.nameInformal;
    case 'EMPLOYEE':
    case 'EMPLOYMENT':
      return showEvents
        ? `${event?.clients?.[0]?.firstName ?? ''} ${
            event?.clients?.[0]?.lastName ?? ''
          }`
        : '';
    case 'WORKPLACE':
      return event?.employees?.[0]?.nameInformal;
    default:
      return null;
  }
}

export default function RosterView({
  kidsRatio,
  isLoading,
  planningEntities,
  setPlanningEntities,
  clonedPlanningEntities,
  setClonedPlanningEntities,
  connectAppointmentGrouping,
  connectSchedule,
  planAppointments,
  revertAction,
  showEvents,
  isMyActivities
}: UsePlanningReturn) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [activeEntityContainingSelection, setActiveEntityContainingSelection] =
    useState<string | null>(null);
  const dateNavigationPeriod = useDateNavigationStore(({ period }) => period);
  const [shouldShowPopoverDialog, setShouldShowPopoverDialog] = useRecoilState(
    shouldShowPopoverDialogAtom
  );
  const selectedPlanUnits = useRecoilValue(planUnitsAtom);
  const [pendingDropPosition, setPendingDropPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const [taskListIsOpen, setTaskListIsOpen] = useState<boolean>(true);

  const multiplePlanUnitsSelected = selectedPlanUnits.length > 1;

  const [activeEvent, setActiveEvent] = useState<Event | null>(null);

  const { hasKidsPlanAccess, hasActivityPlanningEditAccess } = useSelector(
    ({ grants }) => grants
  );
  const { params } = useNavParams();
  const view = params?.view as View;

  const [planningEntityTypePreference] = usePlanningEntity();
  const planningEntityType = planningEntityTypePreference[view];
  const [, setPlanResponsePlanningEntityIdentifiers] = useRecoilState(
    planResponsePlanningEntityIdentifiersAtom
  );
  const [selectedDate] = useSelectedDate();
  const { activityPlanningOnlyPlanned, defaultZoomLevel } = useSelector(
    (state) => state.preferences
  );
  const isDetailed =
    (defaultZoomLevel ?? ZoomLevel.STANDARD) === ZoomLevel.DETAILED;
  const startDate = dayjs(selectedDate).startOf('isoWeek').format('YYYY-MM-DD');

  const isWorkweek = dateNavigationPeriod === 'workweek';
  const weekLength = isWorkweek ? 5 : 7;
  const holidays = useSelector((state) => state.holidays);
  const weekdays = getRosterDays(
    isWorkweek
      ? getWorkweekInterval(selectedDate)
      : getWeekInterval(selectedDate),
    holidays
  );
  const weekNumber = getISOWeek(selectedDate);

  const copyMode: CopyModeReturn = useCopyMode();
  const { endCopyMode } = copyMode;

  useEffect(() => {
    endCopyMode();
  }, []);

  const normalizedData = planningEntities
    ? Object.values(planningEntities)
        ?.filter(
          (planningEntity) =>
            (!activityPlanningOnlyPlanned ||
              planningEntity.events.length ||
              planningEntity.schedules.length) &&
            planningEntity.type !== 'ORGANIZATIONAL_UNIT_TO_PLAN'
        )
        .filter((planningEntity) => {
          return planningEntity.type !== 'EMPLOYEE';
        })
        .map((item) => {
          const itemScheduleEvents = (item.schedules ?? [])
            .map((schedule) => schedule.events)
            .flat();
          const allRawEvents = [...(item.events ?? []), ...itemScheduleEvents];
          const events = getEventsByWeekDay(
            isMyActivities
              ? allRawEvents.filter(
                  (event) =>
                    event?.employees?.some(
                      ({ id }) => id === +getCurrentlyLoggedInEmployeeId()
                    )
                )
              : allRawEvents,
            dateNavigationPeriod
          );

          const schedules = getEventsByWeekDay(
            item.schedules ?? [],
            dateNavigationPeriod
          );
          const rowCount = Object.values(
            item.type === 'EMPLOYMENT' || item.type === 'EMPLOYEE'
              ? schedules
              : events
          ).reduce(
            (acc, cur) => (cur.length > acc.length ? cur : acc),
            []
          ).length;

          const absences =
            item.absences && item.absences.length > 0
              ? getAbsencesOrAvailabilitiesByWeekDay(
                  item.absences,
                  dateNavigationPeriod
                )
              : [];

          const availabilities =
            item.availabilities && item.availabilities.length > 0
              ? getAbsencesOrAvailabilitiesByWeekDay(
                  item.availabilities,
                  dateNavigationPeriod
                )
              : [];
          const metrics = item.metrics ?? undefined;

          const newItem = {
            ...item,
            absences,
            availabilities,
            events,
            schedules,
            rowCount,
            metrics
          };

          return newItem;
        })
    : [];
  //TODO move to generic spot with cardsview
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { distance: 5 } })
  );
  const onMutationError = () => {
    setPayload(undefined);
    revertAction();
    showErrorSnackbar();
    setPendingDropPosition(null);
  };
  const [, setDragActionActive] = useRecoilState<boolean>(dragActionActiveAtom);

  const [payload, setPayload] = useState<
    PlanAppointmentsRequest | ConnectAppointmentGroupingPayload
  >();

  const recentlyMovedToNewContainer = useRef(false);

  const [, setDialogState] = useRecoilState(dialogManagerActiveAtom);

  const { mutateAsync: unassignAppointmentAsync } =
    mutations.useRemoveAssignmentFromAppointments();
  const [
    isLoadingUnassignAppointmentFromEmployee,
    setIsLoadingUnassignAppointmentFromEmployee
  ] = useState<boolean>(false);

  const loading = isLoading || isLoadingUnassignAppointmentFromEmployee;

  const sendConnectSchedule = useConnectScheduleAction({
    connectSchedule,
    defaultScheduleStart: startDate,
    onError: onMutationError,
    planningEntities
  });

  function unassignAppointment(editAppointmentType: EditAppointmentType) {
    unassignAppointmentUtil({
      editAppointmentType,
      activeEvent,
      selectedIds,
      planningEntities,
      shouldShowPopoverDialog,
      appendCorrelationToken,
      unassignAppointmentAsync,
      setShouldShowPopoverDialog,
      setPlanResponsePlanningEntityIdentifiers,
      setActiveEntityContainingSelection,
      setIsLoading: setIsLoadingUnassignAppointmentFromEmployee,
      onFinally: () => {
        setIsLoadingUnassignAppointmentFromEmployee(false);
        setActiveEvent(null);
        setActiveEntityContainingSelection(null);
        setPendingDropPosition(null);
        setSelectedIds([]);
      },
      onGetEvents: (planningEntity, isToPlan, selectedIds) => {
        return (
          (isToPlan
            ? planningEntity?.planningTasks
                ?.map((task: any) => task.data)
                .filter(
                  (event: Event) =>
                    event?.id !== undefined && selectedIds.includes(event.id)
                )
            : planningEntity?.schedules
                .flatMap((s: any) => s.events)
                .filter(({ id }: { id: string }) => selectedIds.includes(id))
          )?.filter(
            (event: Event | undefined): event is Event => event !== undefined
          ) || []
        );
      }
    });
  }

  const handlePlanAppointments = (payload: PlanAppointmentsRequest | null) => {
    handlePlanAppointmentsUtil({
      payload,
      planAppointments,
      setPayload,
      setShouldShowPopoverDialog,
      setDialogState,
      revertAction,
      onMutationError,
      planningEntitiesToEvents,
      getConflictingEvents,
      notificationsToHashTable,
      planningEntities,
      appendCorrelationToken, // Apply correlation token in RosterView
      setPlanResponsePlanningEntityIdentifiers,
      onCancelWarningDialog: () => {
        setActiveEvent(null);
        setPendingDropPosition(null);
      },
      onSuccess: () => {
        setSelectedIds([]);
        setActiveEntityContainingSelection(null);
        setActiveEvent(null);
        setPendingDropPosition(null);
      },
      onComplete: () => {
        setSelectedIds([]);
      }
    });
  };

  function handleDragCancel() {
    setDragActionActive(false);

    if (clonedPlanningEntities) {
      setPlanningEntities(clonedPlanningEntities);
    }
    setActiveEvent(null);
    setPendingDropPosition(null);
    setClonedPlanningEntities(null);
  }

  function handleDragStart({ active }) {
    setDragActionActive(true);

    recentlyMovedToNewContainer.current = false;

    const activePlanningEntityId = active?.data.current.planningEntityId;
    const scheduleId = active?.data.current.scheduleId;
    const activeIsToPlan = active?.data.current.isToPlan;

    const event = !activeIsToPlan
      ? planningEntities?.[activePlanningEntityId]?.schedules
          .map((s) => s.events)
          .flat()
          .find(({ id }) => id === active.id) ?? null
      : planningEntities?.[activePlanningEntityId]?.planningTasks
          ?.map((task) => task.data)
          .find((task): task is Event => (task as Event)?.id === active.id) ??
        null;

    setActiveEvent(event);
    setActiveEntityContainingSelection(scheduleId || activePlanningEntityId);
    setSelectedIds((selected) =>
      selected.includes(active.id) ? selected : []
    );
    setClonedPlanningEntities(planningEntities);
  }

  function handleDragEnd({ active, over }) {
    setDragActionActive(false);
    const overId = over?.id;
    const activePlanningEntityId = active?.data.current.planningEntityId;
    const activeIsToPlan = active?.data.current.isToPlan;
    const overIsToPlan = over?.data.current.isToPlan;
    const isDroppableCell = over?.data.current.isDroppableCell;
    const isDroppablePanelHeader = over?.data.current.isDroppablePanelHeader;

    const planningEntityIdentifier =
      over?.data.current.planningEntityIdentifier;

    if (!over || activeIsToPlan === overIsToPlan)
      return setPendingDropPosition(null);
    if (active.id === overId) return;

    if (
      activePlanningEntityId === undefined ||
      overId === undefined ||
      !activeEvent ||
      (activeIsToPlan === overIsToPlan && !recentlyMovedToNewContainer.current)
    ) {
      setActiveEvent(null);
      setPendingDropPosition(null);
      setSelectedIds([]);
      return;
    }

    const ids = selectedIds.length ? selectedIds : [active.id];

    const selectedEvents: Event[] = ids.map((id) =>
      findItem(planningEntities, activePlanningEntityId, id)
    ) as Event[];

    const mouseUpHandler = (e: MouseEvent) => {
      setPendingDropPosition({
        x: e.clientX,
        y: e.clientY
      });

      // Remove the event listener after it has been triggered
      window.removeEventListener('mouseup', mouseUpHandler);
    };

    window.addEventListener('mouseup', mouseUpHandler, { once: true });

    const occurrenceDate = activeEvent.start
      ? format(new Date(activeEvent.start), 'y-MM-dd')
      : startDate;

    const scheduleStart = over.data.current.scheduleStart;

    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);

    const payload: PlanAppointmentsRequest = {
      appointmentOccurrences:
        selectedIds.length === 0
          ? [
              {
                entityId: activeEvent.id,
                originalOccurrenceStart: activeEvent.start,
                schedulingLabel: activeEvent.schedulingLabel
              }
            ]
          : selectedEvents.map((event: Event) => ({
              entityId: event.id,
              originalOccurrenceStart: event.start,
              schedulingLabel: event.schedulingLabel
            })),
      planningEntity: planningEntityIdentifier,
      start:
        isDroppableCell || isDroppablePanelHeader
          ? null
          : over.data.current.start || activeEvent?.start,
      date:
        (isDroppableCell || isDroppablePanelHeader) && scheduleStart
          ? dayjs(scheduleStart).format('YYYY-MM-DD')
          : '',
      editAppointmentType: 'ONLY_THIS',
      suppressWarnings: false
    };

    setPayload(payload);

    if (activeEvent?.isRecurring && new Date(occurrenceDate) >= today) {
      setShouldShowPopoverDialog({
        id: activeEvent.id,
        scheduleStart: scheduleStart
          ? dayjs(scheduleStart).format('YYYY-MM-DD')
          : '',
        isUnassignment: overIsToPlan,
        isDragAction: true
      });
    } else if (!activeEvent?.isRecurring && overIsToPlan) {
      unassignAppointment('ONLY_THIS');
    } else {
      handlePlanAppointments(payload);
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <PopOverConfirmationContext.Provider
        value={{
          isLoading: loading,
          onClose: () => {
            setActiveEvent(null);
            setPendingDropPosition(null);
            revertAction();
          },
          onHandleAppointmentConfirmation: (
            editAppointmentType: EditAppointmentType,
            isUnassignment?: boolean
          ) => {
            if (isUnassignment) {
              return unassignAppointment(editAppointmentType);
            }

            if (payload && 'appointmentOccurrences' in payload) {
              handlePlanAppointments({
                ...(payload as PlanAppointmentsRequest),
                editAppointmentType
              });
            } else {
              connectAppointmentGrouping(
                {
                  ...payload,
                  editAppointmentType
                } as ConnectAppointmentGroupingPayload,
                {
                  onSuccess: (response: any) => {
                    if (
                      !response.data.success &&
                      payload &&
                      'appointmentGroupingId' in payload &&
                      planningEntities
                    ) {
                      const events =
                        planningEntities[
                          payload.appointmentGroupingId
                        ].schedules
                          .map((s) => s.events)
                          .flat() ?? [];
                      const notifications = response.data
                        .notifications as APNotification[];
                      const conflictingEvents: Event[] = getConflictingEvents(
                        events || [],
                        notifications
                      );

                      setDialogState({
                        type: DialogType.WARNING,
                        open: true,
                        data: notificationsToHashTable(
                          conflictingEvents,
                          notifications
                        ),
                        onSubmit: () => {
                          connectAppointmentGrouping(
                            {
                              ...payload,
                              editAppointmentType,
                              suppressWarnings: true
                            } as ConnectAppointmentGroupingPayload,
                            {
                              onError: onMutationError
                            }
                          );
                        },
                        onCancel: () => {
                          revertAction();
                          setActiveEvent(null);
                        }
                      });
                    } else {
                      setActiveEvent(null);
                    }
                  },
                  onError: onMutationError
                }
              );
            }
          }
        }}
      >
        <LoadingOverlay loading={loading} />
        <Box
          display="flex"
          height="100%"
          overflow="hidden"
          position="relative"
          sx={{
            // Infinite header bar for table in case of large screens
            '&:before': {
              content: '""',
              backgroundColor: GRAY_LIGHT_TERTIARY,
              boxShadow: `inset 0 -1px 0 ${PRIMARY}`,
              width: '100%',
              height: '50px',
              position: 'absolute',
              top: 0
            }
          }}
        >
          {/* Scroll container */}
          <Box
            sx={{
              overflowX: 'scroll',
              height: '100%',
              minWidth: taskListIsOpen ? 'initial' : '100%',
              position: 'relative',
              borderLeft: (theme) => `1px solid ${theme.palette.divider}`
            }}
          >
            <Box display="flex" position="relative" width="100%">
              {/* Table */}
              {normalizedData && (
                <Table sx={{ tableLayout: 'auto', borderCollapse: 'collapse' }}>
                  <tbody>
                    {/* Header row */}
                    <TableHeaderDateRow>
                      {/* Week number */}
                      <TableHeaderDateCell>
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          sx={{
                            position: 'fixed',
                            transform: 'translateY(-50%)',
                            left: '16px',
                            bgcolor: 'text.primary',
                            borderRadius: '50%',
                            color: 'white',
                            width: 26,
                            height: 26
                          }}
                        >
                          {weekNumber}
                        </Box>
                      </TableHeaderDateCell>

                      {/* Days */}
                      {weekdays.map((w: RosterDay, i) => (
                        <TableHeaderDateCell key={i}>
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                          >
                            {(w.isToday || w.isHoliday) && (
                              <Tooltip
                                title={w.description}
                                arrow
                                enterDelay={300}
                                enterNextDelay={300}
                              >
                                <Chip
                                  shape="pill"
                                  variant="filled"
                                  label={`${w.dayName} ${w.day}`}
                                  color={w.isToday ? 'primary' : 'warning'}
                                />
                              </Tooltip>
                            )}
                            {!(w.isHoliday || w.isToday) &&
                              `${w.dayName} ${w.day}`}
                          </Box>
                        </TableHeaderDateCell>
                      ))}
                    </TableHeaderDateRow>

                    {hasKidsPlanAccess &&
                      !showEvents &&
                      kidsRatio &&
                      kidsRatio.totalRatios &&
                      kidsRatio.totalRatios.length > 0 &&
                      planningEntityType === 'EMPLOYEE' && (
                        <KidsTable ratios={kidsRatio} />
                      )}
                    {normalizedData &&
                      normalizedData.length > 0 &&
                      normalizedData
                        .sort(({ name: a }, { name: b, type }) => {
                          if (type === 'EMPLOYEE') return 1;
                          if (type === 'WORKPLACE') return 0;
                          return (a || '').localeCompare(b || '');
                        })
                        .map((item, i: number) => {
                          const previousRow = normalizedData?.[i - 1];
                          return Array.from({
                            length: item.rowCount > 0 ? item.rowCount : 1
                          }).map((_, idx) => {
                            return (
                              <tr
                                key={`${item.id}${idx}`}
                                {...(previousRow &&
                                  item.type !== previousRow?.type && {
                                    style: {
                                      borderTop: `1px solid ${PRIMARY}`
                                    }
                                  })}
                              >
                                {idx === 0 && (
                                  <TableCell
                                    style={{
                                      verticalAlign: 'top',
                                      minWidth: '100px',
                                      position: 'sticky',
                                      left: 0,
                                      zIndex: 30,
                                      background: `linear-gradient(90deg, var(--mui-palette-background-default) 70%, transparent)`,
                                      padding: '0 8px'
                                    }}
                                    rowSpan={
                                      item.rowCount > 0 ? item.rowCount : 1
                                    }
                                  >
                                    {idx === 0 && (
                                      <Box justify-content="space-between">
                                        <Box mt={1}>
                                          <Typography>
                                            {item?.name ?? 'Geen medewerker'}
                                          </Typography>

                                          {item?.description && (
                                            <Typography variant="subtitle2">
                                              {item.description}
                                            </Typography>
                                          )}
                                        </Box>
                                        {item?.metrics &&
                                          planningEntityType === 'EMPLOYEE' && (
                                            <Box
                                              mt={1}
                                              sx={{ marginTop: '-5px' }}
                                              display="flex"
                                            >
                                              <Typography variant="subtitle2">
                                                Contract:{' '}
                                                {formatHours(
                                                  item.metrics
                                                    .contractDurationInSeconds ??
                                                    0
                                                )}
                                              </Typography>
                                              <Box width={'20px'} />
                                              <Typography variant="subtitle2">
                                                Gepland:{' '}
                                                {formatHours(
                                                  item.metrics
                                                    .plannedDurationInSeconds ??
                                                    0
                                                )}
                                              </Typography>
                                            </Box>
                                          )}
                                      </Box>
                                    )}
                                  </TableCell>
                                )}
                                {Array.from({ length: weekLength }).map(
                                  (_, weekday) => {
                                    const schedule: Schedule =
                                      item.schedules?.[weekday + 1]?.[idx];
                                    const currentDay = dayjs(startDate).add(
                                      weekday,
                                      'day'
                                    );
                                    const absence: AbsenceResponse =
                                      item.absences?.[
                                        currentDay.format('DD-MM-YYYY')
                                      ];

                                    const availability: AvailabilityResponse =
                                      item.availabilities?.[
                                        currentDay.format('DD-MM-YYYY')
                                      ];

                                    const showAvailability =
                                      availability &&
                                      availability?.type === 'NOT_AVAILABLE';

                                    // For <DroppableCell/> and <Absence/>
                                    const getDroppableProps = (id: string) => ({
                                      id: `${id}-${weekday}-${idx}-${item.id}`,
                                      planningEntityId: item.id,
                                      planningEntityIdentifier:
                                        item.planningEntityIdentifier!,
                                      planningEntityType: item.type,
                                      scheduleStart: dayjs(startDate)
                                        .add(weekday, 'day')
                                        .format('YYYY-MM-DD')
                                    });

                                    if (
                                      showEvents &&
                                      planningEntityType === 'EMPLOYEE'
                                    ) {
                                      if (
                                        schedule ||
                                        absence ||
                                        showAvailability
                                      ) {
                                        return (
                                          <TableCell
                                            key={`${idx}-${weekday}`}
                                            style={{
                                              minWidth: `${CARD_WIDTH}px`,
                                              verticalAlign: 'top'
                                            }}
                                          >
                                            {absence && (
                                              <Absence
                                                {...getDroppableProps(
                                                  'absence'
                                                )}
                                                absence={absence}
                                                showTime={
                                                  // We only show times when there is only ONE day of absence and it is shorter than 24 hours
                                                  // or when there are multiple absences but they are not sequential
                                                  Math.abs(
                                                    dayjs(absence.start).diff(
                                                      absence.end,
                                                      'minutes'
                                                    )
                                                  ) <
                                                    60 * 24 - 1 ||
                                                  // When the absences are MORE than one day, but the first or last day are shorter than 24 hours
                                                  (currentDay.isoWeekday() ===
                                                    dayjs(
                                                      absence.start
                                                    ).isoWeekday() &&
                                                    isWithinDay(
                                                      absence.start
                                                    )) ||
                                                  (currentDay.isoWeekday() ===
                                                    dayjs(
                                                      absence.end
                                                    ).isoWeekday() &&
                                                    isWithinDay(absence.end))
                                                }
                                              />
                                            )}

                                            {showAvailability && (
                                              <Availability
                                                {...getDroppableProps(
                                                  'availability'
                                                )}
                                                availability={availability}
                                                showTime={
                                                  // We only show times when there is only ONE day of absence and it is shorter than 24 hours
                                                  // or when there are multiple availability but they are not sequential
                                                  Math.abs(
                                                    dayjs(
                                                      availability.start
                                                    ).diff(
                                                      availability.end,
                                                      'minutes'
                                                    )
                                                  ) <
                                                    60 * 24 - 1 ||
                                                  // When the availability are MORE than one day, but the first or last day are shorter than 24 hours
                                                  (currentDay.isoWeekday() ===
                                                    dayjs(
                                                      availability.start
                                                    ).isoWeekday() &&
                                                    isWithinDay(
                                                      availability.start
                                                    )) ||
                                                  (currentDay.isoWeekday() ===
                                                    dayjs(
                                                      availability.end
                                                    ).isoWeekday() &&
                                                    isWithinDay(
                                                      availability.end
                                                    ))
                                                }
                                              />
                                            )}

                                            <Box
                                              sx={{
                                                height:
                                                  absence && showAvailability
                                                    ? 'calc(100% - 100px)'
                                                    : absence ||
                                                      showAvailability
                                                    ? 'calc(100% - 50px)'
                                                    : '100%'
                                              }}
                                            >
                                              {schedule && (
                                                <ActivityPanel
                                                  id={`${weekday}-${idx}-${item.id}`}
                                                  key={`${weekday}-${idx}-${item.id}`}
                                                  readOnly={
                                                    !hasActivityPlanningEditAccess
                                                  }
                                                  name={schedule.name}
                                                  headerName={schedule.name}
                                                  showDuty={false}
                                                  showDurationDistanceTransport={
                                                    isDetailed
                                                  }
                                                  {...schedule}
                                                  planningEntityType={item.type}
                                                  planningEntityId={
                                                    item
                                                      .planningEntityIdentifier
                                                      ?.entityId ??
                                                    item
                                                      .planningEntityIdentifier
                                                      ?.externalEntityId ??
                                                    item.id ??
                                                    ''
                                                  }
                                                  planningEntityIdentifier={
                                                    item.planningEntityIdentifier
                                                  }
                                                  isOverrulingExternalDuty={
                                                    schedule.isOverrulingExternalDuty
                                                  }
                                                  activeEntityContainingSelection={
                                                    activeEntityContainingSelection
                                                  }
                                                  onChangeActiveEntity={
                                                    setActiveEntityContainingSelection
                                                  }
                                                  onChangeSelection={
                                                    setSelectedIds
                                                  }
                                                  onConnectScheduleClick={() =>
                                                    sendConnectSchedule(
                                                      item.planningEntityIdentifier,
                                                      schedule.start
                                                    )
                                                  }
                                                />
                                              )}
                                            </Box>
                                          </TableCell>
                                        );
                                      }

                                      return (
                                        <TableCell
                                          key={`${idx}-${weekday}`}
                                          style={{
                                            minWidth: `${CARD_WIDTH}px`
                                          }}
                                        >
                                          {item &&
                                          planningEntityType === 'EMPLOYEE' ? (
                                            <DroppableCell
                                              {...getDroppableProps('cell')}
                                            />
                                          ) : (
                                            <EventPlaceholder
                                              key={`${idx}-${weekday}`}
                                              date={dayjs(startDate)
                                                .add(weekday, 'day')
                                                .toISOString()}
                                            />
                                          )}
                                        </TableCell>
                                      );
                                    }

                                    let event =
                                      showEvents ||
                                      planningEntityType !== 'EMPLOYEE'
                                        ? item.events?.[weekday + 1]?.[idx]
                                        : item.schedules?.[weekday + 1]?.[idx];
                                    if (
                                      planningEntityType === 'PROJECT' &&
                                      item.type === 'EMPLOYEE'
                                    ) {
                                      event =
                                        item.schedules?.[weekday + 1]?.[idx];
                                    }

                                    const isPlannedForDate = dayjs(
                                      (event as Event)?.date
                                    ).isSame(
                                      dayjs(startDate).add(weekday, 'day'),
                                      'date'
                                    );

                                    if (!isPlannedForDate) {
                                      event = null;
                                    }

                                    const subtitle = getSubtitle(
                                      event,
                                      planningEntityType,
                                      showEvents
                                    );

                                    return (
                                      <TableCell
                                        key={`${idx}-${weekday}`}
                                        style={{
                                          minWidth: `${CARD_WIDTH}px`
                                        }}
                                      >
                                        {(() => {
                                          if (event) {
                                            switch (planningEntityType) {
                                              case 'PROJECT':
                                                if (item.type === 'PROJECT') {
                                                  return (
                                                    <ConditionalDraggableCard
                                                      {...event}
                                                      planningEntityType={
                                                        item.type
                                                      }
                                                      component="div"
                                                      isAlwaysActivityCard
                                                      copyMode={copyMode}
                                                    />
                                                  );
                                                }
                                                return (
                                                  <EventCard
                                                    title={
                                                      event.duty?.dutyCode?.code
                                                    }
                                                    subtitle={subtitle}
                                                    event={event}
                                                    showActions={false}
                                                  />
                                                );
                                              default:
                                                if (showEvents) {
                                                  return (
                                                    <ConditionalDraggableCard
                                                      {...event}
                                                      planningEntityType={
                                                        item.type
                                                      }
                                                      component="div"
                                                      isAlwaysActivityCard
                                                      copyMode={copyMode}
                                                    />
                                                  );
                                                }
                                                return (
                                                  <EventCard
                                                    title={
                                                      event.duty?.dutyCode?.code
                                                    }
                                                    multiplePlanUnitsSelected={
                                                      multiplePlanUnitsSelected
                                                    }
                                                    subtitle={subtitle}
                                                    event={event}
                                                    showActions={false}
                                                  />
                                                );
                                            }
                                          } else {
                                            // Placeholders
                                            switch (planningEntityType) {
                                              case 'WORKPLACE':
                                                return (
                                                  <EventPlaceholder
                                                    workPlaceEntityId={item?.id}
                                                    date={dayjs(startDate)
                                                      .add(weekday, 'day')
                                                      .toISOString()}
                                                  />
                                                );
                                              case 'PROJECT':
                                                return (
                                                  <EventPlaceholder
                                                    projectEntityId={item?.id}
                                                    date={dayjs(startDate)
                                                      .add(weekday, 'day')
                                                      .toISOString()}
                                                  />
                                                );
                                              default:
                                                return (
                                                  <EventPlaceholder
                                                    date={dayjs(startDate)
                                                      .add(weekday, 'day')
                                                      .toISOString()}
                                                  />
                                                );
                                            }
                                          }
                                        })()}
                                      </TableCell>
                                    );
                                  }
                                )}
                              </tr>
                            );
                          });
                        })}
                  </tbody>
                </Table>
              )}
            </Box>
          </Box>

          {planningEntityType === 'EMPLOYEE' && showEvents && (
            <TaskList
              onOpen={setTaskListIsOpen.bind(undefined, true)}
              onClose={setTaskListIsOpen.bind(undefined, false)}
              planningEntities={planningEntities}
              dropzonePadding="10px"
              activeEntityContainingSelection={activeEntityContainingSelection}
              onChangeActiveEntity={setActiveEntityContainingSelection}
              onChangeSelection={setSelectedIds}
              DropzoneComponent={ToPlanDropzone}
              SortedToPlanListComponent={SortedToPlanList}
            />
          )}
        </Box>
        {activeEvent && (
          <DragPortal
            activeEvent={activeEvent}
            selectedIds={selectedIds}
            pendingDropPosition={pendingDropPosition}
            dropAnimation={dropAnimation}
          />
        )}
      </PopOverConfirmationContext.Provider>
    </DndContext>
  );
}
